'use client';
import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// // Swiper の CSS をインポート
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import FeaturedJob from '~/components/jobs/featured-job';
import ErrorBoundary from '~/components/error-boundary';
import { JobDetail } from '~/types/job-detail';

interface FeaturedJobsProps {
  jobs: JobDetail[];
  displayJobSize: number;
  gtmClass: string;
  isMobile: boolean;
  isSignedIn: boolean;
  favoriteJobIds: number[];
}

const FeaturedJobs: React.FC<FeaturedJobsProps> = ({
  jobs,
  displayJobSize,
  gtmClass,
  isMobile,
  isSignedIn,
  favoriteJobIds,
}) => {
  // jobs 配列を displayJobSize ごとにチャンクに分割するヘルパー関数
  const chunkJobs = (items: JobDetail[], size: number): JobDetail[][] => {
    const chunks: JobDetail[][] = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  };

  // スライドが必要かどうかを判定
  const hasPagination = jobs.length > displayJobSize;

  // スライドが不要な場合は通常表示
  if (!hasPagination) {
    return (
      <div className="p-featured-jobs">
        {jobs.map((job) => (
          <ErrorBoundary key={job.id}>
            <FeaturedJob
              key={job.id}
              job={job}
              gtmClass={gtmClass}
              isMobile={isMobile}
              isSignedIn={isSignedIn}
              favoriteJobIds={favoriteJobIds}
            />
          </ErrorBoundary>
        ))}
      </div>
    );
  }

  // チャンク分割した配列からスライドを生成する
  const jobGroups = chunkJobs(jobs, displayJobSize);

  // お気に入りなら -attention-jobs、それ以外は -jobs
  const containerClass = gtmClass.includes('favorite')
    ? '-attention-jobs'
    : '-jobs';

  // お気に入りの場合のみ autoplay を設定する例
  const autoplayConfig =
    containerClass === '-attention-jobs' ? { delay: 4500 } : false;

  return (
    <div className={`swiper-container ${containerClass}`}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        pagination={{
          el: '.swiper-pagination',
          type: 'bullets',
          clickable: true,
        }}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }}
        autoplay={autoplayConfig}
        loop
        spaceBetween={18}
        slidesPerView="auto"
        breakpoints={{
          450: {
            slidesPerView: 1,
          },
        }}
        className="u-pb24" // Swiper ルート要素に追加したいクラス
      >
        {/* チャンクごとに 1 スライドを生成。1 スライド内で複数の FeaturedJob を表示 */}
        {jobGroups.map((group, index) => (
          <SwiperSlide key={index} className="p-featured-jobs">
            {group.map((job) => (
              <ErrorBoundary key={job.id}>
                <FeaturedJob
                  key={job.id}
                  job={job}
                  gtmClass={gtmClass}
                  isMobile={isMobile}
                  isSignedIn={isSignedIn}
                  favoriteJobIds={favoriteJobIds}
                />
              </ErrorBoundary>
            ))}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Swiper ページネーション要素 */}
      <div className="swiper-pagination" />

      {/* 前へ/次へボタン */}
      <div className="u-pos-rel u-pt4">
        <div className="swiper-button-prev" />
        <div className="swiper-button-next" />
      </div>
    </div>
  );
};

export default FeaturedJobs;
